import {
    CheckOutlined,
    CloseOutlined,
    DownloadOutlined,
    InboxOutlined,
    RightOutlined,
    SaveOutlined,
    SearchOutlined,
    SwapOutlined,
    ThunderboltOutlined,
    UndoOutlined,
    BarChartOutlined,
} from '@ant-design/icons';
import { Button, Input, message, Modal, Select, Spin, Tag, Tooltip } from 'antd';
import ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc, where } from 'firebase/firestore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth } from '../../../context/DataContext';
import { db } from '../../../firebase';
import apis from '../../..//apis';
import './SessionAssignmentManager.css';

const ITEM_TYPE = 'REGISTRANT_CARD';

// Session colors for visual distinction
const SESSION_COLORS = [
    '#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6',
    '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#a855f7',
    '#10b981', '#e11d48', '#0ea5e9', '#eab308', '#7c3aed',
];

// --- Helper: Get performer name from registrant ---
const getRegistrantName = (reg) => {
    if (!reg) return 'Unknown';
    // Old format (Registrants collection)
    if (reg.name && !reg.performers) return reg.name;
    // New format (Registrants2025)
    const p = reg.performers?.[0];
    if (!p) return reg.name || 'Unknown';
    return p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown';
};

const getRegistrantEmail = (reg) => {
    if (!reg) return '';
    const p = reg.performers?.[0];
    return p?.email || reg.email || '';
};

// ======================================================
// DraggableCard Component
// ======================================================
const DraggableCard = ({ registrant, index, sessionId, moveWithinSession, onRemove, isSwapMode, isSwapSelected, onSwapSelect }) => {
    const ref = useRef(null);
    const name = getRegistrantName(registrant);
    const achievement = registrant.finalAward || registrant.achievement || '';
    const achievementClass = achievement.toLowerCase();

    const [{ isDragging }, drag] = useDrag({
        type: ITEM_TYPE,
        item: () => ({ registrantId: registrant.id, sourceSessionId: sessionId, index }),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(item, monitor) {
            if (!ref.current || !sessionId || item.sourceSessionId !== sessionId) return;
            const dragIdx = item.index;
            const hoverIdx = index;
            if (dragIdx === hoverIdx) return;

            const hoverRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverRect.top;

            if (dragIdx < hoverIdx && hoverClientY < hoverMiddleY) return;
            if (dragIdx > hoverIdx && hoverClientY > hoverMiddleY) return;

            moveWithinSession(sessionId, dragIdx, hoverIdx);
            item.index = hoverIdx;
        },
    });

    drag(drop(ref));

    return (
        <div
            ref={ref}
            className={`sam-registrant-card ${isDragging ? 'dragging' : ''} ${isSwapSelected ? 'swap-selected' : ''}`}
            onClick={isSwapMode ? () => onSwapSelect(registrant.id, sessionId) : undefined}
            style={{ cursor: isSwapMode ? 'pointer' : 'grab' }}
        >
            {sessionId && <div className="order-number">{index + 1}</div>}
            <div className="card-info">
                <div className="card-name">{name}</div>
                <div className="card-details">
                    {achievement && <span className={`achievement-tag ${achievementClass}`}>{achievement}</span>}
                    {registrant.teacherName && <span>🎓 {registrant.teacherName}</span>}
                    {registrant.repertoireTitle && <span>🎼 {registrant.repertoireTitle}</span>}
                    {registrant.competitionCategory && <span>🎵 {registrant.competitionCategory}</span>}
                    {registrant.duration && <span>⏱ {registrant.duration}</span>}
                </div>
            </div>
            <div className="card-actions">
                {sessionId && onRemove && (
                    <Tooltip title="Remove from session">
                        <Button size="small" type="text" danger icon={<CloseOutlined />}
                            onClick={(e) => { e.stopPropagation(); onRemove(registrant.id, sessionId); }} />
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

// ======================================================
// SessionDropZone Component
// ======================================================
const SessionDropZone = ({ sessionId, sessionName, sessionColor, registrants, index: sessionIndex,
    moveWithinSession, onDropFromPool, onRemove, isExpanded, onToggle,
    isSwapMode, swapSelections, onSwapSelect }) => {

    const [{ isOver }, drop] = useDrop({
        accept: ITEM_TYPE,
        drop: (item) => {
            if (item.sourceSessionId !== sessionId) {
                onDropFromPool(item.registrantId, item.sourceSessionId, sessionId);
            }
        },
        collect: (monitor) => ({ isOver: monitor.isOver() }),
    });

    return (
        <div className={`sam-session-card ${isOver ? 'drag-over' : ''}`}>
            <div className="sam-session-header" onClick={onToggle}>
                <div className="sam-session-header-left">
                    <div className="session-color-dot" style={{ background: sessionColor }} />
                    <h4>{sessionName}</h4>
                </div>
                <div className="sam-session-header-right">
                    <div className="sam-session-meta">
                        <Tag className="sam-session-meta-tag" color="blue">{registrants.length} registrants</Tag>
                    </div>
                    <RightOutlined className={`sam-session-expand-icon ${isExpanded ? 'expanded' : ''}`} />
                </div>
            </div>
            {isExpanded && (
                <div ref={drop} className={`sam-session-body ${isOver ? 'drag-over' : ''}`}>
                    {registrants.length === 0 ? (
                        <div className="sam-session-body-empty">
                            <InboxOutlined style={{ marginRight: 8 }} /> Drop registrants here
                        </div>
                    ) : (
                        registrants.map((reg, idx) => (
                            <DraggableCard
                                key={reg.id}
                                registrant={reg}
                                index={idx}
                                sessionId={sessionId}
                                moveWithinSession={moveWithinSession}
                                onRemove={onRemove}
                                isSwapMode={isSwapMode}
                                isSwapSelected={swapSelections.some(s => s.registrantId === reg.id)}
                                onSwapSelect={onSwapSelect}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ======================================================
// Main SessionAssignmentManager Component
// ======================================================
const SessionAssignmentManager = () => {
    const { currentEventId } = useAuth();

    // --- State ---
    const [eventId, setEventId] = useState(currentEventId || 'APCS2026');
    const [allRegistrants, setAllRegistrants] = useState([]);
    const [sessions, setSessions] = useState([]); // [{id, name}]
    const [assignments, setAssignments] = useState({}); // {sessionId: [registrantObj, ...]}
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [expandedSessions, setExpandedSessions] = useState({});
    const [isSwapMode, setIsSwapMode] = useState(false);
    const [swapSelections, setSwapSelections] = useState([]); // [{registrantId, sessionId}]
    const [isDirty, setIsDirty] = useState(false);
    const [initialAssignments, setInitialAssignments] = useState(null);
    const [isOverviewModalOpen, setIsOverviewModalOpen] = useState(false);
    const [overviewVenue, setOverviewVenue] = useState(null);

    // --- Derived: assigned registrant IDs ---
    const assignedIds = useMemo(() => {
        const ids = new Set();
        Object.values(assignments).forEach(regs => regs.forEach(r => ids.add(r.id)));
        return ids;
    }, [assignments]);

    // --- Derived: unassigned registrants ---
    const unassignedRegistrants = useMemo(() => {
        let filtered = allRegistrants.filter(r => !assignedIds.has(r.id));
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            filtered = filtered.filter(r => {
                const name = getRegistrantName(r).toLowerCase();
                const teacher = (r.teacherName || '').toLowerCase();
                return name.includes(lower) || teacher.includes(lower);
            });
        }
        if (categoryFilter) {
            filtered = filtered.filter(r => r.competitionCategory === categoryFilter);
        }
        return filtered;
    }, [allRegistrants, assignedIds, searchTerm, categoryFilter]);

    // --- Derived: category options ---
    const categoryOptions = useMemo(() => {
        const cats = new Set();
        allRegistrants.forEach(r => { if (r.competitionCategory) cats.add(r.competitionCategory); });
        return Array.from(cats).sort().map(c => ({ value: c, label: c }));
    }, [allRegistrants]);

    // --- Derived: Overview — unique venues and per-venue sessions ---
    const overviewVenues = useMemo(() => {
        const map = {};
        sessions.forEach(s => {
            if (!map[s.venue]) map[s.venue] = s.venueLabel || s.venue;
        });
        return Object.entries(map).map(([id, label]) => ({ id, label }));
    }, [sessions]);

    const overviewSessionsForVenue = useMemo(() => {
        if (!overviewVenue) return [];
        return sessions.filter(s => s.venue === overviewVenue);
    }, [sessions, overviewVenue]);

    // --- Fetch sessions from events/{eventId} ---
    const fetchSessions = useCallback(async (eid) => {
        try {
            const eventDoc = await getDoc(doc(db, 'events', eid));
            if (!eventDoc.exists()) {
                message.warning(`No event found for ${eid}`);
                setSessions([]);
                return;
            }
            const data = eventDoc.data();
            const sessionList = [];
            const venueList = data.venues || [];

            // Sessions are now nested inside venues array in events/{eventId}
            venueList.forEach(venue => {
                const venueSessions = venue.sessions || {};
                const venueLabel = venue.label || venue.id;

                Object.keys(venueSessions).forEach(date => {
                    const times = venueSessions[date] || [];
                    times.forEach(time => {
                        const id = `${venue.id}_${date}_${time}`;
                        sessionList.push({
                            id,
                            name: `${venueLabel} — ${date} — ${time}`,
                            venue: venue.id,
                            venueLabel,
                            date,
                            time,
                        });
                    });
                });
            });

            // Sort by date then time
            sessionList.sort((a, b) => {
                if (a.date !== b.date) return a.date.localeCompare(b.date);
                return a.time.localeCompare(b.time);
            });

            setSessions(sessionList);
            // Expand first session by default
            if (sessionList.length > 0) {
                setExpandedSessions({ [sessionList[0].id]: true });
            }
        } catch (err) {
            console.error('Error fetching sessions:', err);
            message.error('Failed to load sessions');
        }
    }, []);

    // --- Fetch registrants ---
    const fetchRegistrants = useCallback(async (eid) => {
        try {
            const q = query(collection(db, 'Registrants2025'), orderBy('createdAt'));
            const snap = await getDocs(q);
            let docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
            // Filter by eventId if field exists
            if (eid) {
                docs = docs.filter(d => !d.eventId || d.eventId === eid);
            }
            setAllRegistrants(docs);
        } catch (err) {
            console.error('Error fetching registrants:', err);
            message.error('Failed to load registrants');
        }
    }, []);

    // --- Load saved assignments ---
    const loadAssignments = useCallback(async (eid, registrantMap) => {
        try {
            const res = await apis.session.getAssignments(eid);
            if (res && res.data && res.data.assignments) {
                const saved = res.data.assignments || {};
                // Rehydrate: replace stale snapshots with current registrant data
                const hydrated = {};
                Object.keys(saved).forEach(sessionId => {
                    hydrated[sessionId] = (saved[sessionId] || [])
                        .map(entry => registrantMap[entry.registrantId])
                        .filter(Boolean);
                });
                setAssignments(hydrated);
                setInitialAssignments(hydrated);
                setIsDirty(false);
                message.success('Loaded saved assignments');
            } else {
                setInitialAssignments({});
                setIsDirty(false);
            }
        } catch (err) {
            console.error('Error loading assignments:', err);
        }
    }, []);

    // --- Warn on unsaved changes ---
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // --- Check if dirty ---
    useEffect(() => {
        if (initialAssignments === null) return;
        
        // Simple deep equality check via stringify for this structure
        const current = JSON.stringify(assignments);
        const initial = JSON.stringify(initialAssignments);
        setIsDirty(current !== initial);
    }, [assignments, initialAssignments]);

    // --- Initial load ---
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setAssignments({});
            await fetchSessions(eventId);
            await fetchRegistrants(eventId);
            setLoading(false);
        };
        init();
    }, [eventId, fetchSessions, fetchRegistrants]);

    // --- After registrants are loaded, try to load saved assignments ---
    useEffect(() => {
        if (allRegistrants.length > 0 && !loading) {
            const map = {};
            allRegistrants.forEach(r => { map[r.id] = r; });
            loadAssignments(eventId, map);
        }
    }, [allRegistrants, loading, eventId, loadAssignments]);

    // --- DnD: Move within same session (reorder) ---
    const moveWithinSession = useCallback((sessionId, dragIdx, hoverIdx) => {
        setAssignments(prev => {
            const regs = [...(prev[sessionId] || [])];
            const [removed] = regs.splice(dragIdx, 1);
            regs.splice(hoverIdx, 0, removed);
            return { ...prev, [sessionId]: regs };
        });
    }, []);

    // --- DnD: Drop from pool or another session ---
    const handleDrop = useCallback((registrantId, sourceSessionId, targetSessionId) => {
        setAssignments(prev => {
            const next = { ...prev };
            // Remove from source
            if (sourceSessionId && next[sourceSessionId]) {
                next[sourceSessionId] = next[sourceSessionId].filter(r => r.id !== registrantId);
            }
            // Find registrant
            const reg = allRegistrants.find(r => r.id === registrantId);
            if (!reg) return prev;
            // Add to target
            next[targetSessionId] = [...(next[targetSessionId] || []), reg];
            return next;
        });
    }, [allRegistrants]);

    // --- Remove from session ---
    const handleRemove = useCallback((registrantId, sessionId) => {
        setAssignments(prev => ({
            ...prev,
            [sessionId]: (prev[sessionId] || []).filter(r => r.id !== registrantId),
        }));
    }, []);

    // --- Swap logic ---
    const handleSwapSelect = useCallback((registrantId, sessionId) => {
        setSwapSelections(prev => {
            const exists = prev.findIndex(s => s.registrantId === registrantId);
            if (exists >= 0) return prev.filter((_, i) => i !== exists);
            if (prev.length >= 2) return [{ registrantId, sessionId }];
            return [...prev, { registrantId, sessionId }];
        });
    }, []);

    const executeSwap = useCallback(() => {
        if (swapSelections.length !== 2) return;
        const [a, b] = swapSelections;
        setAssignments(prev => {
            const next = { ...prev };
            const listA = [...(next[a.sessionId] || [])];
            const listB = [...(next[b.sessionId] || [])];
            const idxA = listA.findIndex(r => r.id === a.registrantId);
            const idxB = listB.findIndex(r => r.id === b.registrantId);
            if (idxA < 0 || idxB < 0) return prev;

            if (a.sessionId === b.sessionId) {
                [listA[idxA], listA[idxB]] = [listA[idxB], listA[idxA]];
                next[a.sessionId] = listA;
            } else {
                const temp = listA[idxA];
                listA[idxA] = listB[idxB];
                listB[idxB] = temp;
                next[a.sessionId] = listA;
                next[b.sessionId] = listB;
            }
            return next;
        });
        setSwapSelections([]);
        message.success('Swapped successfully!');
    }, [swapSelections]);

    // --- Auto-assign ---
    const handleAutoAssign = useCallback(() => {
        if (sessions.length === 0) { message.warning('No sessions available'); return; }
        const unassigned = allRegistrants.filter(r => !assignedIds.has(r.id));
        if (unassigned.length === 0) { message.info('All registrants are already assigned'); return; }

        const perSession = Math.ceil(unassigned.length / sessions.length);
        const newAssignments = { ...assignments };
        let idx = 0;
        sessions.forEach(session => {
            const existing = newAssignments[session.id] || [];
            const toAdd = unassigned.slice(idx, idx + perSession);
            newAssignments[session.id] = [...existing, ...toAdd];
            idx += perSession;
        });
        setAssignments(newAssignments);
        message.success(`Auto-assigned ${unassigned.length} registrants across ${sessions.length} sessions`);
    }, [sessions, allRegistrants, assignedIds, assignments]);

    // --- Save to Firestore ---
    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            // Serialize: store only registrantId + name + order
            const serialized = {};
            Object.keys(assignments).forEach(sessionId => {
                serialized[sessionId] = (assignments[sessionId] || []).map((r, idx) => ({
                    registrantId: r.id,
                    name: getRegistrantName(r),
                    email: getRegistrantEmail(r),
                    order: idx,
                    competitionCategory: r.competitionCategory || '',
                    teacherName: r.teacherName || '',
                    repertoireTitle: r.repertoireTitle || '',
                }));
            });
            await apis.session.saveAssignments({
                eventId,
                assignments: serialized,
            });
            message.success('Assignments saved successfully!');
            setInitialAssignments(assignments);
            setIsDirty(false);
        } catch (err) {
            console.error('Error saving:', err);
            message.error('Failed to save assignments');
        } finally {
            setSaving(false);
        }
    }, [assignments, eventId]);

    // --- Discard changes ---
    const handleDiscard = useCallback(() => {
        Modal.confirm({
            title: 'Discard all unsaved changes?',
            content: 'This will revert all assignments to the last saved state.',
            okText: 'Discard',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                setAssignments(initialAssignments);
                message.info('Changes discarded');
            },
        });
    }, [initialAssignments]);

    // --- Clear all ---
    const handleClearAll = useCallback(() => {
        Modal.confirm({
            title: 'Clear all assignments?',
            content: 'This will remove all registrants from all sessions. You will still need to save to apply these changes.',
            okText: 'Clear All',
            okType: 'danger',
            onOk() {
                setAssignments({});
                message.info('All assignments cleared locally');
            },
        });
    }, []);

    // --- Change Event with confirmation ---
    const handleEventChange = useCallback((newId) => {
        if (isDirty) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes in the current event. Changing events will discard these changes. Continue?',
                okText: 'Discard & Change',
                okType: 'danger',
                onOk() {
                    setEventId(newId);
                },
            });
        } else {
            setEventId(newId);
        }
    }, [isDirty]);

    // --- Export to Excel ---
    const handleExport = useCallback(() => {
        const workbook = new ExcelJS.Workbook();
        sessions.forEach(session => {
            const regs = assignments[session.id] || [];
            const ws = workbook.addWorksheet(session.name.substring(0, 31)); // Excel max 31 chars
            ws.addRow(['#', 'Name', 'Email', 'Teacher', 'Category', 'Achievement']);
            ws.getRow(1).font = { bold: true };
            regs.forEach((r, i) => {
                ws.addRow([
                    i + 1,
                    getRegistrantName(r),
                    getRegistrantEmail(r),
                    r.teacherName || '',
                    r.competitionCategory || '',
                    r.finalAward || r.achievement || '',
                ]);
            });
            ws.columns.forEach(col => { col.width = 20; });
        });
        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            FileSaver.saveAs(blob, `SessionAssignments_${eventId}.xlsx`);
        });
    }, [sessions, assignments, eventId]);

    // --- Toggle session expand ---
    const toggleSession = useCallback((sessionId) => {
        setExpandedSessions(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
    }, []);

    // --- Render ---
    if (loading) {
        return <div className="sam-loading-overlay"><Spin size="large" tip="Loading session data..." /></div>;
    }

    const totalAssigned = assignedIds.size;
    const totalUnassigned = allRegistrants.length - totalAssigned;

    return (
        <DndProvider backend={HTML5Backend}>
            <div>
                {/* === Controls Bar === */}
                <div className="sam-controls-bar">
                    <Select
                        value={eventId}
                        onChange={handleEventChange}
                        style={{ width: 180 }}
                        options={[
                            { value: 'APCS2026', label: 'APCS 2026' },
                            { value: 'APCS2025', label: 'APCS 2025' },
                        ]}
                    />
                    {isDirty && (
                        <Tag color="orange" className="sam-editing-indicator">
                            <ThunderboltOutlined spin /> Editing Mode
                        </Tag>
                    )}
                    <div className="sam-stats-row">
                        <div className="sam-stat-badge total">📊 Total: {allRegistrants.length}</div>
                        <div className="sam-stat-badge assigned">✅ Assigned: {totalAssigned}</div>
                        <div className="sam-stat-badge unassigned">⏳ Unassigned: {totalUnassigned}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <Button className="sam-auto-assign-btn" icon={<ThunderboltOutlined />}
                            onClick={handleAutoAssign} style={{ background: '#6366f1', color: '#fff', border: 'none' }}>
                            Auto-Assign
                        </Button>
                        <Button icon={<BarChartOutlined />} onClick={() => {
                            setOverviewVenue(overviewVenues.length > 0 ? overviewVenues[0].id : null);
                            setIsOverviewModalOpen(true);
                        }}>
                            Overview
                        </Button>
                        <Button icon={<SwapOutlined />}
                            type={isSwapMode ? 'primary' : 'default'}
                            onClick={() => { setIsSwapMode(!isSwapMode); setSwapSelections([]); }}
                            style={isSwapMode ? { background: '#8b5cf6', borderColor: '#8b5cf6' } : {}}>
                            {isSwapMode ? 'Exit Swap' : 'Swap Mode'}
                        </Button>
                        <Button icon={<UndoOutlined />} onClick={handleClearAll}>Clear All</Button>
                        {isDirty && (
                            <Button icon={<CloseOutlined />} onClick={handleDiscard}>Discard</Button>
                        )}
                        <Button icon={<SaveOutlined />} type="primary" loading={saving} onClick={handleSave}
                            style={isDirty ? { background: '#10b981', borderColor: '#10b981', boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)' } : {}}>
                            Save {isDirty ? 'Changes' : ''}
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
                    </div>
                </div>

                {/* === Swap Bar === */}
                {isSwapMode && (
                    <div className="sam-swap-bar">
                        <SwapOutlined />
                        <span>
                            {swapSelections.length === 0 && 'Click two registrants to swap their positions'}
                            {swapSelections.length === 1 && 'Now click the second registrant to swap with'}
                            {swapSelections.length === 2 && 'Ready to swap!'}
                        </span>
                        {swapSelections.length === 2 && (
                            <Button size="small" icon={<CheckOutlined />} onClick={executeSwap}
                                style={{ background: '#fff', color: '#6366f1', fontWeight: 700 }}>
                                Confirm Swap
                            </Button>
                        )}
                    </div>
                )}

                {/* === Two-Panel Layout === */}
                <div className="sam-layout">
                    {/* --- Left Panel: Unassigned Pool --- */}
                    <div className="sam-pool-panel">
                        <div className="sam-pool-header">
                            <h3>
                                Unassigned Registrants
                                <span className="count-badge">{unassignedRegistrants.length}</span>
                            </h3>
                            <div className="sam-pool-filters">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search name or teacher..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    allowClear
                                    size="small"
                                    style={{ flex: 1 }}
                                />
                                <Select
                                    placeholder="Category"
                                    value={categoryFilter}
                                    onChange={setCategoryFilter}
                                    allowClear
                                    size="small"
                                    style={{ width: 140 }}
                                    options={categoryOptions}
                                />
                            </div>
                        </div>
                        <div className="sam-pool-list">
                            {unassignedRegistrants.length === 0 ? (
                                <div className="sam-pool-empty">
                                    <InboxOutlined />
                                    <span>
                                        {allRegistrants.length === 0
                                            ? 'No registrants found for this event'
                                            : 'All registrants have been assigned!'}
                                    </span>
                                </div>
                            ) : (
                                unassignedRegistrants.map((reg) => (
                                    <DraggableCard
                                        key={reg.id}
                                        registrant={reg}
                                        index={0}
                                        sessionId={null}
                                        moveWithinSession={() => { }}
                                        isSwapMode={false}
                                        isSwapSelected={false}
                                        onSwapSelect={() => { }}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* --- Right Panel: Sessions --- */}
                    <div className="sam-sessions-panel">
                        {sessions.length === 0 ? (
                            <div className="sam-pool-empty" style={{ padding: 60 }}>
                                <InboxOutlined />
                                <span>No sessions found in <strong>events/{eventId}</strong></span>
                            </div>
                        ) : (
                            sessions.map((session, idx) => (
                                <SessionDropZone
                                    key={session.id}
                                    sessionId={session.id}
                                    sessionName={session.name}
                                    sessionColor={SESSION_COLORS[idx % SESSION_COLORS.length]}
                                    registrants={assignments[session.id] || []}
                                    index={idx}
                                    moveWithinSession={moveWithinSession}
                                    onDropFromPool={handleDrop}
                                    onRemove={handleRemove}
                                    isExpanded={!!expandedSessions[session.id]}
                                    onToggle={() => toggleSession(session.id)}
                                    isSwapMode={isSwapMode}
                                    swapSelections={swapSelections}
                                    onSwapSelect={handleSwapSelect}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* === Overview Modal === */}
                <Modal
                    title="Session Overview"
                    open={isOverviewModalOpen}
                    onCancel={() => setIsOverviewModalOpen(false)}
                    footer={[
                        <Button key="close" onClick={() => setIsOverviewModalOpen(false)}>
                            Close
                        </Button>
                    ]}
                    width="95vw"
                    style={{ top: 20 }}
                    styles={{ body: { maxHeight: '80vh', overflowY: 'auto', padding: '16px' } }}
                >
                    {/* Venue Tabs */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        {overviewVenues.map(v => (
                            <Button
                                key={v.id}
                                type={overviewVenue === v.id ? 'primary' : 'default'}
                                onClick={() => setOverviewVenue(v.id)}
                                style={overviewVenue === v.id
                                    ? { background: '#6366f1', borderColor: '#6366f1' }
                                    : {}}
                            >
                                {v.label}
                            </Button>
                        ))}
                    </div>

                    {!overviewVenue ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                            Select a venue above to view its sessions.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 8 }}>
                            {overviewSessionsForVenue.map((session, idx) => {
                                const regs = assignments[session.id] || [];
                                const sessionColor = SESSION_COLORS[idx % SESSION_COLORS.length];
                                return (
                                    <div key={session.id} style={{
                                        minWidth: 260, maxWidth: 300, flex: '0 0 auto',
                                        background: '#f7f8fa', borderRadius: 10,
                                        border: '1px solid #e8e8e8', overflow: 'hidden'
                                    }}>
                                        {/* Column Header */}
                                        <div style={{
                                            padding: '10px 14px',
                                            background: `linear-gradient(135deg, ${sessionColor}22, ${sessionColor}11)`,
                                            borderBottom: `3px solid ${sessionColor}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{session.date}</div>
                                                <div style={{ fontSize: 12, color: '#555' }}>{session.time}</div>
                                            </div>
                                            <Tag color="blue" style={{ marginRight: 0 }}>{regs.length}</Tag>
                                        </div>
                                        {/* Registrant List */}
                                        <div style={{ padding: '8px 10px', maxHeight: 500, overflowY: 'auto' }}>
                                            {regs.length === 0 ? (
                                                <div style={{ color: '#aaa', fontSize: 12, textAlign: 'center', padding: 20 }}>No registrants</div>
                                            ) : (
                                                regs.map((reg, i) => {
                                                    const name = getRegistrantName(reg);
                                                    const award = reg.finalAward || reg.achievement || '';
                                                    const awardLower = award.toLowerCase();
                                                    const awardColor =
                                                        awardLower === 'sapphire' ? '#2563eb' :
                                                        awardLower === 'diamond' ? '#7c3aed' :
                                                        awardLower === 'gold' ? '#d97706' :
                                                        awardLower === 'silver' ? '#6b7280' :
                                                        awardLower === 'bronze' ? '#b45309' : '#888';
                                                    return (
                                                        <div key={reg.id} style={{
                                                            display: 'flex', alignItems: 'flex-start', gap: 8,
                                                            padding: '6px 8px', marginBottom: 4,
                                                            background: '#fff', borderRadius: 6,
                                                            border: '1px solid #eee', fontSize: 12
                                                        }}>
                                                            <span style={{
                                                                minWidth: 20, height: 20, borderRadius: '50%',
                                                                background: sessionColor, color: '#fff',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 10, fontWeight: 700, flexShrink: 0
                                                            }}>{i + 1}</span>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {name}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                                                                    {award && (
                                                                        <span style={{
                                                                            display: 'inline-block', padding: '1px 6px',
                                                                            borderRadius: 4, fontSize: 10, fontWeight: 600,
                                                                            background: `${awardColor}18`, color: awardColor,
                                                                            border: `1px solid ${awardColor}33`
                                                                        }}>{award}</span>
                                                                    )}
                                                                </div>
                                                                {reg.teacherName && (
                                                                    <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>🎓 {reg.teacherName}</div>
                                                                )}
                                                                {reg.repertoireTitle && (
                                                                    <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>🎼 {reg.repertoireTitle}</div>
                                                                )}
                                                                {reg.competitionCategory && (
                                                                    <div style={{ color: '#999', fontSize: 10, marginTop: 1 }}>🎵 {reg.competitionCategory}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal>
            </div>
        </DndProvider>
    );
};

export default SessionAssignmentManager;
